import React, { useMemo, useState } from "react";
import { ChevronDown, Search as SearchIcon } from "lucide-react";

// The filter row above the results. Four controls stay visible and the rest sit
// behind "More filters" — showing all ten at once reads like a tax form and
// puts people off using it at all.
//
// Chips follow the app's control style exactly (see the Columns button in
// ProjectsLayout): font-outfit text-[14px], px-3 py-1.5, rounded, #00000036
// border at rest, #BB42AE / #F8F3FF when active.

export const EMPTY_FILTERS = {
  // "open" | "closed" | "all". A choice rather than a checkbox, because the
  // rail shows a real count against each one.
  status: "open",
  closing_within_days: null,
  // The closing filter keeps tenders whose deadline was never published. The
  // stat tile counts only dated ones, so it turns this on to ask for those.
  require_deadline: false,
  notice_type: [],
  category: [],
  state: [],
  buyer: [],
  posted_since_days: null,
  set_aside_only: false,
  has_documents: false,
  only_imported: false,
  // An industry filter means that industry. Tenders the agency left without a
  // code are left out, and the results screen says how many and offers to put
  // them back. Including them by default swamped the sub-industries: Cloud &
  // hosting holds 481 and the filter returned 1,327.
  include_unclassified: false,
};

export const DATE_PRESETS = [
  { days: 1, label: "Last 24 hours" },
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

// SAM.gov publishes place of performance as a two-letter code, so the options
// have to be codes too. Territories are included: they carry real tenders.
export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
  ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ["PR", "Puerto Rico"], ["VI", "US Virgin Islands"], ["GU", "Guam"],
  ["AS", "American Samoa"], ["MP", "Northern Mariana Islands"],
];

// Built from the local calendar date, not toISOString(): that converts to UTC
// first, so anyone west of Greenwich asking for "last 24 hours" late in the day
// would silently get a window starting a day early.
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// A real agency name can contain a literal comma — "Veterans Affairs,
// Department of" is one of ours — so it cannot share the comma-joined format
// every other list filter here uses. Matches BUYER_SEPARATOR on the backend.
const BUYER_SEPARATOR = "\x1f";

// Only the values the user actually chose are sent. An empty list must not
// become `category=`, which the backend would read as a filter matching nothing.
export const buildSearchParams = (filters, { keywords, ordering }) => {
  const p = { ordering };
  if (keywords?.trim()) p.q = keywords.trim();
  if (filters.closing_within_days != null) p.closing_within_days = filters.closing_within_days;
  if (filters.require_deadline) p.require_deadline = true;
  if (filters.notice_type.length) p.notice_type = filters.notice_type.join(",");
  if (filters.category.length) p.category = filters.category.join(",");
  if (filters.state.length) p.state = filters.state.join(",");
  if (filters.buyer.length) p.buyer = filters.buyer.join(BUYER_SEPARATOR);
  if (filters.posted_since_days) p.posted_since = isoDaysAgo(filters.posted_since_days);
  if (filters.set_aside_only) p.set_aside_only = true;
  if (filters.has_documents) p.has_documents = true;
  if (filters.only_imported) p.only_imported = true;
  if (filters.status === "closed") p.closed_only = true;
  else if (filters.status === "all") p.include_closed = true;
  if (filters.include_unclassified) p.include_unclassified = true;
  return p;
};

// The URL is what the search screen restores from, so a filter set survives
// opening a tender, browser back, a refresh and a pasted link. Kept separate
// from buildSearchParams because the API wants an absolute posted_since date
// while the URL should keep the relative choice the user actually made.
export const DEFAULT_ORDERING = "closing_soon";

export const filtersToUrl = (filters, { keywords, ordering }) => {
  const p = new URLSearchParams();
  if (keywords?.trim()) p.set("q", keywords.trim());
  if (ordering && ordering !== DEFAULT_ORDERING) p.set("sort", ordering);
  if (filters.closing_within_days != null)
    p.set("closing_within_days", String(filters.closing_within_days));
  if (filters.require_deadline) p.set("require_deadline", "1");
  if (filters.notice_type.length) p.set("notice_type", filters.notice_type.join(","));
  if (filters.category.length) p.set("category", filters.category.join(","));
  if (filters.state.length) p.set("state", filters.state.join(","));
  if (filters.buyer.length) p.set("buyer", filters.buyer.join(BUYER_SEPARATOR));
  if (filters.posted_since_days) p.set("posted_since_days", String(filters.posted_since_days));
  if (filters.set_aside_only) p.set("set_aside_only", "1");
  if (filters.has_documents) p.set("has_documents", "1");
  if (filters.only_imported) p.set("only_imported", "1");
  if (filters.status && filters.status !== "open") p.set("status", filters.status);
  if (filters.include_unclassified) p.set("include_unclassified", "1");
  return p;
};

export const filtersFromUrl = (search) => {
  const p = new URLSearchParams(search);
  const list = (key) => (p.get(key) ? p.get(key).split(",").filter(Boolean) : []);
  const buyerList = (key) =>
    p.get(key) ? p.get(key).split(BUYER_SEPARATOR).filter(Boolean) : [];
  const number = (key) => {
    const raw = p.get(key);
    return raw !== null && raw !== "" && Number.isFinite(Number(raw)) ? Number(raw) : null;
  };
  const flag = (key, fallback = false) => {
    const raw = p.get(key);
    return raw === null ? fallback : raw === "1" || raw === "true";
  };

  return {
    keywords: p.get("q") || "",
    ordering: p.get("sort") || DEFAULT_ORDERING,
    filters: {
      closing_within_days: number("closing_within_days"),
      require_deadline: flag("require_deadline"),
      notice_type: list("notice_type"),
      category: list("category"),
      state: list("state"),
      buyer: buyerList("buyer"),
      posted_since_days: number("posted_since_days"),
      set_aside_only: flag("set_aside_only"),
      has_documents: flag("has_documents"),
      only_imported: flag("only_imported"),
      // include_closed=1 is what links made before the status control carry.
      status: ["closed", "all"].includes(p.get("status"))
        ? p.get("status")
        : flag("include_closed")
          ? "all"
          : "open",
      include_unclassified: flag("include_unclassified", false),
    },
  };
};

// Counts choices, not sections. Ticking three notice types and a state is four
// filters to the person who ticked them; counting the two sections and saying
// "2 filters on" reads as a bug when four boxes are visibly on.
//
// require_deadline is skipped: nobody picks it. It travels with a closing date
// preset, and counting it would make one click add two.
const UNCOUNTED = new Set(["require_deadline"]);

export const countActiveFilters = (filters) =>
  Object.keys(EMPTY_FILTERS).reduce((total, key) => {
    if (UNCOUNTED.has(key)) return total;
    const value = filters[key];
    const base = EMPTY_FILTERS[key];
    if (Array.isArray(value)) return total + value.length;
    return value !== base ? total + 1 : total;
  }, 0);

const chipClass = (on) =>
  `inline-flex items-center gap-1.5 font-outfit text-[14px] px-3 py-1.5 border rounded transition-colors ${
    on
      ? "border-[#BB42AE] text-[#BB42AE] bg-[#F8F3FF] font-medium"
      : "border-[#00000036] text-[#52525B] bg-white hover:bg-gray-50"
  }`;

function CountBadge({ n }) {
  if (!n) return null;
  return (
    <span className="bg-[#BB42AE] text-white text-[10px] font-semibold rounded-full px-1.5 py-[1px] leading-[15px]">
      {n}
    </span>
  );
}

function ToggleChip({ label, on, onClick, title }) {
  return (
    <button type="button" title={title} onClick={onClick} className={chipClass(on)}>
      {label}
    </button>
  );
}

function DropdownChip({ label, selectedCount, width = 262, children }) {
  const [open, setOpen] = useState(false);
  const on = selectedCount > 0;

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={chipClass(on || open)}>
        {label}
        <CountBadge n={selectedCount} />
        <ChevronDown
          className={`w-3.5 h-3.5 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[998] bg-transparent" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-[calc(100%+6px)] z-[999] bg-white border border-black/20
                       rounded-[12px] shadow-lg p-3"
            style={{ width }}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function CheckRow({ checked, label, hint, onToggle }) {
  return (
    <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="w-[18px] h-[18px] rounded cursor-pointer shrink-0"
        style={{ accentColor: "#8B55DD" }}
      />
      <span className="font-outfit text-[14px] font-medium text-[#1A1A1A] flex-1 truncate">
        {label}
      </span>
      {hint != null && <span className="font-outfit text-[12px] text-[#9CA3AF]">{hint}</span>}
    </label>
  );
}

function PanelFooter({ onClear, disabled }) {
  return (
    <button
      type="button"
      onClick={onClear}
      disabled={disabled}
      className="mt-2 pt-2 w-full text-left font-outfit text-[13px] font-medium text-[#BB42AE]
                 border-t border-[#F1F1F3] disabled:text-[#9CA3AF]"
    >
      Clear
    </button>
  );
}

export default function TenderFilters({ filters, onChange, facets }) {
  const [showMore, setShowMore] = useState(false);
  const [stateQuery, setStateQuery] = useState("");
  const [buyerQuery, setBuyerQuery] = useState("");

  const set = (patch) => onChange({ ...filters, ...patch });

  const toggleIn = (key, value) => {
    const current = filters[key];
    set({
      [key]: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  };

  const noticeTypes = facets?.notice_types || [];
  const categories = facets?.categories || [];
  const byNoticeType = facets?.counts?.by_notice_type || {};
  const biddable = noticeTypes.filter((t) => t.biddable).map((t) => t.value);

  const allBiddableOn =
    biddable.length > 0 && biddable.every((v) => filters.notice_type.includes(v));

  const visibleStates = useMemo(() => {
    const q = stateQuery.trim().toLowerCase();
    if (!q) return US_STATES;
    return US_STATES.filter(([code, name]) => name.toLowerCase().includes(q) || code.toLowerCase() === q);
  }, [stateQuery]);

  const buyers = useMemo(() => facets?.counts?.options?.buyer || [], [facets]);
  const visibleBuyers = useMemo(() => {
    const q = buyerQuery.trim().toLowerCase();
    const list = q ? buyers.filter((b) => b.name.toLowerCase().includes(q)) : buyers;
    // Ticked ones stay visible even once search narrows the list past them,
    // or clearing a filter you can no longer see reads as the box being stuck.
    const ticked = buyers.filter((b) => filters.buyer.includes(b.name) && !list.includes(b));
    return [...ticked, ...list].slice(0, 60);
  }, [buyers, buyerQuery, filters.buyer]);

  const datePreset = DATE_PRESETS.find((p) => p.days === filters.posted_since_days);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 flex-wrap items-center">
        <ToggleChip
          label="Closing in 30 days"
          on={filters.closing_within_days === 30}
          onClick={() =>
            set({ closing_within_days: filters.closing_within_days === 30 ? null : 30 })
          }
        />

        <DropdownChip label="Type" selectedCount={filters.notice_type.length} width={280}>
          <button
            type="button"
            onClick={() => set({ notice_type: allBiddableOn ? [] : biddable })}
            className="w-full text-left font-outfit text-[13px] font-medium text-[#BB42AE] pb-2 mb-1
                       border-b border-[#F1F1F3]"
          >
            {allBiddableOn ? "Clear" : "Open for bidding"}
          </button>
          <div className="max-h-[248px] overflow-y-auto custom-scrollbar pr-1">
            {noticeTypes.map((t) => (
              <CheckRow
                key={t.value}
                checked={filters.notice_type.includes(t.value)}
                label={t.label}
                hint={byNoticeType[t.value]?.toLocaleString()}
                onToggle={() => toggleIn("notice_type", t.value)}
              />
            ))}
          </div>
        </DropdownChip>

        <DropdownChip label="Industry" selectedCount={filters.category.length} width={272}>
          <div className="max-h-[268px] overflow-y-auto custom-scrollbar pr-1">
            {categories.length === 0 && (
              <p className="font-outfit text-[13px] text-[#9CA3AF] py-1">
                No industries available.
              </p>
            )}
            {categories.map((c) => (
              <CheckRow
                key={c.slug}
                checked={filters.category.includes(c.slug)}
                label={c.label}
                onToggle={() => toggleIn("category", c.slug)}
              />
            ))}
          </div>
          <PanelFooter onClear={() => set({ category: [] })} disabled={!filters.category.length} />
        </DropdownChip>

        <DropdownChip label="Location" selectedCount={filters.state.length} width={272}>
          <div className="relative mb-2">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
            <input
              value={stateQuery}
              onChange={(e) => setStateQuery(e.target.value)}
              placeholder="Find a state"
              className="w-full font-outfit text-[13px] pl-8 pr-2.5 py-1.5 border border-[#00000036]
                         rounded-md outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE]"
            />
          </div>
          <div className="max-h-[212px] overflow-y-auto custom-scrollbar pr-1">
            {visibleStates.map(([code, name]) => (
              <CheckRow
                key={code}
                checked={filters.state.includes(code)}
                label={name}
                hint={code}
                onToggle={() => toggleIn("state", code)}
              />
            ))}
          </div>
          {/* Most federal tenders name no place of performance at all, and a
              location filter drops every one of them. Saying so here is
              cheaper than a user concluding the search is broken. */}
          <p className="mt-2 pt-2 border-t border-[#F1F1F3] font-outfit text-[12px] text-[#71717A] leading-[1.45]">
            Around 7 in 10 federal tenders publish no place of performance. This filter cannot
            match those.
          </p>
          <PanelFooter onClear={() => set({ state: [] })} disabled={!filters.state.length} />
        </DropdownChip>

        <DropdownChip label="Organization" selectedCount={filters.buyer.length} width={300}>
          <div className="relative mb-2">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
            <input
              value={buyerQuery}
              onChange={(e) => setBuyerQuery(e.target.value)}
              placeholder="Find an agency"
              className="w-full font-outfit text-[13px] pl-8 pr-2.5 py-1.5 border border-[#00000036]
                         rounded-md outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE]"
            />
          </div>
          <div className="max-h-[268px] overflow-y-auto custom-scrollbar pr-1">
            {visibleBuyers.length === 0 && (
              <p className="font-outfit text-[13px] text-[#9CA3AF] py-1">
                {buyerQuery ? "No agency matches that." : "No agencies available."}
              </p>
            )}
            {visibleBuyers.map((b) => (
              <CheckRow
                key={b.name}
                checked={filters.buyer.includes(b.name)}
                label={b.name}
                hint={b.count?.toLocaleString()}
                onToggle={() => toggleIn("buyer", b.name)}
              />
            ))}
          </div>
          <PanelFooter onClear={() => set({ buyer: [] })} disabled={!filters.buyer.length} />
        </DropdownChip>

        <ToggleChip
          label="Set-aside only"
          on={filters.set_aside_only}
          onClick={() => set({ set_aside_only: !filters.set_aside_only })}
        />

        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="font-outfit text-[13px] text-[#BB42AE] font-medium px-2 py-1.5"
        >
          {showMore ? "Fewer filters" : "+ More filters"}
        </button>
      </div>

      {showMore && (
        <div className="flex gap-2 flex-wrap items-center">
          <DropdownChip
            label={datePreset ? `Posted: ${datePreset.label}` : "Date posted"}
            selectedCount={filters.posted_since_days ? 1 : 0}
            width={200}
          >
            {DATE_PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() =>
                  set({ posted_since_days: filters.posted_since_days === p.days ? null : p.days })
                }
                className={`w-full text-left font-outfit text-[14px] px-2 py-1.5 rounded-md ${
                  filters.posted_since_days === p.days
                    ? "bg-[#F8F3FF] text-[#BB42AE] font-medium"
                    : "text-[#1A1A1A] font-medium hover:bg-[#F5F5F5]"
                }`}
              >
                {p.label}
              </button>
            ))}
            <PanelFooter
              onClear={() => set({ posted_since_days: null })}
              disabled={!filters.posted_since_days}
            />
          </DropdownChip>

          <ToggleChip
            label="Has documents"
            on={filters.has_documents}
            onClick={() => set({ has_documents: !filters.has_documents })}
          />
          <ToggleChip
            label="Include closed"
            on={filters.status === "all"}
            onClick={() => set({ status: filters.status === "all" ? "open" : "all" })}
          />
        </div>
      )}
    </div>
  );
}
